import React from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Package, MapPin, Clock, AlertTriangle, Mail, Building2, Navigation } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import LiveTrackingMap from "../components/LiveTrackingMap";

const STATUS_COLORS = {
  created: "bg-slate-100 text-slate-800",
  ready_for_pickup: "bg-blue-100 text-blue-800",
  picked_up: "bg-indigo-100 text-indigo-800",
  at_hub: "bg-purple-100 text-purple-800",
  sorted: "bg-purple-100 text-purple-800",
  out_for_delivery: "bg-amber-100 text-amber-800",
  delivered: "bg-emerald-100 text-emerald-800",
  failed_delivery: "bg-red-100 text-red-800",
  redelivery_requested: "bg-orange-100 text-orange-800",
  manual_review: "bg-blue-100 text-blue-800",
  returned: "bg-gray-200 text-gray-800",
  cancelled: "bg-gray-100 text-gray-600",
};

export default function PublicTrackingPage() {
  const { id } = useParams();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [live, setLive] = React.useState(null);
  const [liveLoading, setLiveLoading] = React.useState(false);

  const loadLive = React.useCallback(async () => {
    if (!id) return;
    setLiveLoading(true);
    try {
      const { data: l } = await axios.get(`${API}/track/${id}/live`);
      setLive(l);
    } catch (e) {
      setLive({ available: false, reason: "Live position er ikke tilgængelig" });
    } finally {
      setLiveLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/track/${id}`);
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Forsendelsen blev ikke fundet");
      } finally { setLoading(false); }
    })();
  }, [id]);

  // Auto-poll live position every 25 s while out_for_delivery
  React.useEffect(() => {
    if (data?.status !== "out_for_delivery") return;
    loadLive();
    const t = setInterval(loadLive, 25000);
    return () => clearInterval(t);
  }, [data?.status, loadLive]);

  if (loading) return (
    <div className="min-h-screen bg-brand-bg2 flex items-center justify-center px-4">
      <div className="text-brand-text2 flex items-center gap-2"><Loader2 className="animate-spin" size={18}/> Indlæser tracking-info...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-brand-bg2 flex items-center justify-center px-4">
      <div className="max-w-md text-center" data-testid="public-track-error">
        <Package size={48} className="mx-auto text-brand-text2 mb-3"/>
        <h1 className="font-display font-bold text-2xl text-brand-dark mb-2">Vi kunne ikke finde forsendelsen</h1>
        <p className="text-sm text-brand-text2">{error}</p>
        <p className="text-xs text-brand-text2 mt-4">Tjek tracking-linket eller kontakt EMA Kurer.</p>
        <a href="mailto:kontakt@emakurer.dk" className="btn-primary mt-4 inline-flex">Kontakt EMA</a>
      </div>
    </div>
  );

  const color = STATUS_COLORS[data.status] || "bg-gray-100 text-gray-700";

  return (
    <div className="min-h-screen bg-brand-bg2 py-8 px-4" data-testid="public-tracking-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <Link to="/" className="inline-flex items-center gap-2 text-brand-dark mb-6 hover:opacity-80">
          <Building2 size={20}/>
          <span className="font-display font-bold text-xl">EMA Kurer</span>
        </Link>

        {/* Main card */}
        <div className="bg-white border border-brand p-6 mb-4">
          <div className="text-xs uppercase tracking-wider text-brand-red font-semibold">Tracking</div>
          <div className="font-mono font-bold text-2xl text-brand-dark mt-1" data-testid="public-track-number">{data.shipment_number}</div>
          {data.is_sandbox && <div className="inline-block text-xs px-2 py-0.5 bg-amber-100 text-amber-800 mt-2">TEST / SANDBOX</div>}

          <div className="mt-4">
            <div className={`inline-block px-3 py-1 ${color} text-sm font-semibold`} data-testid="public-track-status">
              {data.status_label}
            </div>
            <p className="text-brand-text2 mt-2 text-sm">{data.status_description}</p>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-3 text-sm">
            <Info icon={Package} label="Service" value={data.product_label}/>
            <Info icon={MapPin} label="Leveringsområde" value={`${data.delivery_postal_code} ${data.delivery_city}`}/>
            <Info icon={Clock} label="Oprettet" value={data.created_at_iso ? new Date(data.created_at_iso).toLocaleString("da-DK") : "—"}/>
            {data.delivered_at_iso && <Info icon={Clock} label="Leveret" value={new Date(data.delivered_at_iso).toLocaleString("da-DK")}/>}
          </div>

          {(data.status === "failed_delivery" || data.status === "redelivery_requested" || data.status === "manual_review" || data.status === "returned") && (
            <div className="mt-4 bg-red-50 border border-red-200 p-4 text-sm flex items-start gap-3" data-testid="public-track-failed">
              <AlertTriangle className="text-red-600 mt-0.5 flex-shrink-0" size={18}/>
              <div className="flex-1">
                <div className="font-semibold text-red-800 mb-1">
                  {data.status === "failed_delivery" && "Levering ikke gennemført"}
                  {data.status === "redelivery_requested" && "Genlevering på vej"}
                  {data.status === "manual_review" && "Sagen behandles"}
                  {data.status === "returned" && "Pakken er returneret til afsender"}
                </div>
                {data.failed_delivery_reason_text && (
                  <p className="text-red-700 text-xs mt-1"><b>Årsag:</b> {data.failed_delivery_reason_text}</p>
                )}
                {data.next_step_text && (
                  <p className="text-red-700 text-xs mt-1">{data.next_step_text}</p>
                )}
                <p className="text-red-700 text-xs mt-2">Spørgsmål? <a href="tel:+4591309334" className="underline font-semibold">+45 91 30 93 34</a> · <a href="mailto:kontakt@emakurer.dk" className="underline font-semibold">kontakt@emakurer.dk</a></p>
              </div>
            </div>
          )}
        </div>

        {/* Live tracking map — only while out_for_delivery */}
        {data.status === "out_for_delivery" && (
          <div className="bg-white border border-brand p-6 mb-4" data-testid="public-track-live-section">
            <div className="flex items-center gap-2 mb-3">
              <Navigation size={16} className="text-brand-red"/>
              <h2 className="font-display font-bold text-lg text-brand-dark">Live position</h2>
            </div>
            <LiveTrackingMap
              live={live}
              onRefresh={loadLive}
              refreshing={liveLoading}
              testId="public-live-map"
            />
          </div>
        )}

        {/* Timeline */}
        <div className="bg-white border border-brand p-6">
          <h2 className="font-display font-bold text-lg text-brand-dark mb-4">Forsendelses-historik</h2>
          <ol className="relative border-l-2 border-brand-bg2 pl-5 space-y-4" data-testid="public-track-timeline">
            {data.events?.length === 0 && <li className="text-sm text-brand-text2">Endnu ingen opdateringer</li>}
            {data.events?.map((ev, i) => {
              const c = STATUS_COLORS[ev.status] || "bg-gray-100 text-gray-700";
              const latest = i === data.events.length - 1;
              return (
                <li key={i} className="relative">
                  <span className={`absolute -left-7 top-1 w-3 h-3 rounded-full ${latest ? "bg-brand-red" : "bg-brand-text2"} border-2 border-white`}></span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 ${c}`}>{ev.status_label}</span>
                    <span className="text-xs text-brand-text2">{ev.ts_iso ? new Date(ev.ts_iso).toLocaleString("da-DK") : ""}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Contact card */}
        <div className="bg-white border border-brand p-5 mt-4 text-sm">
          <h3 className="font-display font-bold text-base text-brand-dark mb-2">Spørgsmål til levering?</h3>
          <p className="text-brand-text2">Kontakt EMA Kurer hvis du har brug for hjælp med din forsendelse.</p>
          <a href="mailto:kontakt@emakurer.dk" className="inline-flex items-center gap-1 mt-3 text-brand-red underline" data-testid="public-track-contact">
            <Mail size={14}/> kontakt@emakurer.dk
          </a>
        </div>

        <div className="text-center mt-6 text-xs text-brand-text2">
          EMA Kurer ApS · CVR 45016715 · emakurer.dk
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-xs uppercase text-brand-text2">{label}</div>
      <div className="text-sm font-semibold text-brand-dark flex items-center gap-1 mt-0.5"><Icon size={12}/>{value}</div>
    </div>
  );
}

